import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useCallback } from "react";
import { useMedia } from "../../../../hooks";
import { PasswordChecks } from "./utils";
/**
 * @typedef {object} PasswordStrengthProps
 * @property {((password: string) => boolean|string)[]} [checks] `true` when valid, or when invalid, `false` when not-required, else error-message
 * @property {string} password
 */

/**
 * @type {React.FC<PasswordStrengthProps & { [key: string]: any }>}
 */

export const PasswordStrength = ({
  checks,
  password
}) => {
  const strengthPercentage = checks.reduce((sum, fn) => sum + (fn(password) === true ? 1 : 0), 0) / checks.length * 100;
  const message = checks.map(fn => fn(password)).find(res => typeof res === "string");
  const strength = strengthPercentage === 100 ? "Strong" : strengthPercentage >= 50 ? "Average" : "Weak";
  const Thermometer = useCallback(() => /*#__PURE__*/React.createElement("div", {
    className: "w-full inline-block sm:w-2/3"
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames("flex w-full rounded-sm border ", {
      "border-en-red": strength === "Weak",
      "border-en-yellow-800": strength === "Average",
      "border-en-green": strength === "Strong"
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames("inline-block p-1", {
      "bg-en-red": strength === "Weak",
      "bg-en-yellow-800": strength === "Average",
      "bg-en-green": strength === "Strong"
    }),
    style: {
      width: `${strengthPercentage}%`
    }
  }))), [strengthPercentage]);
  const Strength = useCallback(() => /*#__PURE__*/React.createElement("div", {
    className: "block w-full p-1 text-right sm:inline-block sm:w-1/3"
  }, strength), [strength]);
  const Components = [Thermometer, Strength];
  const indicators = useMedia(["(min-width: 640px)"], [[1, 0]], [0, 1]);
  return /*#__PURE__*/React.createElement("div", {
    className: "flex w-full text-xs text-left"
  }, /*#__PURE__*/React.createElement("div", {
    className: "inline-block w-7/12 font-light text-en-gray-500 py-1"
  }, message), /*#__PURE__*/React.createElement("div", {
    className: "inline-block w-5/12 py-1 sm:py-0"
  }, indicators.map(i => {
    const Component = Components[i];
    return /*#__PURE__*/React.createElement(Component, {
      key: i
    });
  })));
};
PasswordStrength.defaultProps = {
  checks: [...[PasswordChecks.hasDigit, PasswordChecks.hasLowercase, PasswordChecks.hasUppercase, PasswordChecks.hasSpecial].map(fn =>
  /** @param {string} password */
  password => {
    const result = fn(password);
    return typeof result === "string" ? false : result;
  }), password => PasswordChecks.hasMinimumLength(password, 12)]
};
PasswordStrength.propTypes = {
  checks: PropTypes.array,
  password: PropTypes.string.isRequired
};
export * from "./utils";